import { API_BASE } from '../config';
import { noteSessionEnd } from './sessionEnd';
import { clearSession, loadSession, saveSession } from './tokens';
import type { ApiError, TokenResponse } from './types';

export class RequestFailed extends Error {
  constructor(public readonly status: number, public readonly error: ApiError) {
    super(error.message);
  }
}

/**
 * Запрос не дошёл: связь оборвалась.
 *
 * Отдельно от {@link RequestFailed} намеренно. Разница принципиальная:
 * отказ сервера может означать «сессии больше нет», а обрыв связи не
 * означает ничего, кроме обрыва, и выбрасывать человека из аккаунта
 * по такому поводу нельзя.
 */
export class NetworkFailed extends Error {
  constructor() {
    super('Нет связи с сервером');
  }
}

type Options = {
  method?: string;
  body?: unknown;
  auth?: boolean;
};

/**
 * Обновление токена — единственное в момент времени. Без этого несколько
 * параллельных запросов, получивших 401, устроили бы шторм из refresh, и все,
 * кроме первого, получили бы «токен уже использован» с отзывом всех сессий.
 */
/**
 * Итог обновления токена.
 * <p>
 * Различать «сессия отозвана» и «сеть не работает» обязательно: если считать
 * любую неудачу отзывом, приложение выкидывает человека из аккаунта при каждом
 * обрыве связи — на телефоне это происходит постоянно.
 */
export type RefreshOutcome =
  | { status: 'ok'; token: string }
  | { status: 'unauthorized' }
  | { status: 'network' };

let refreshInFlight: Promise<RefreshOutcome> | null = null;

/**
 * Кого известить, когда сервер отклонил сессию.
 * <p>
 * Раньше решение «пора на экран входа» принимали несколько мест сразу,
 * включая обработчик соединения, — и именно он выбрасывал человека, когда
 * обновление токена не доходило до сервера. Теперь источник один: прямой
 * отказ сервера, а остальные только слушают.
 */
const sessionRejected: Array<() => void> = [];

export function onSessionRejected(listener: () => void): () => void {
  sessionRejected.push(listener);
  return () => {
    const index = sessionRejected.indexOf(listener);
    if (index >= 0) sessionRejected.splice(index, 1);
  };
}

export async function refreshSession(): Promise<RefreshOutcome> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const session = loadSession();
    if (!session) {
      noteSessionEnd('сессия не нашлась в хранилище');
      return { status: 'unauthorized' } as const;
    }
    try {
      const response = await fetch(API_BASE + '/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: session.refreshToken }),
      });
      if (response.status >= 500) {
        // Сервер перезапускается или упал — сессия здесь ни при чём
        return { status: 'network' } as const;
      }
      if (!response.ok) {
        // Единственное место во всём приложении, где заканчивается сессия
        // не по нажатию человека. Сервер прямо сказал, что токен не годится —
        // продолжать нечего, и спорить не с чем.
        noteSessionEnd(`сервер отклонил сессию (${response.status})`);
        clearSession();
        sessionRejected.forEach((listener) => listener());
        return { status: 'unauthorized' } as const;
      }
      const tokens = (await response.json()) as TokenResponse;
      saveSession(tokens);
      return { status: 'ok', token: tokens.accessToken } as const;
    } catch {
      // fetch бросает только на сетевых сбоях: сессию не трогаем
      return { status: 'network' } as const;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

async function once(path: string, options: Options, token: string | null): Promise<Response> {
  return fetch(API_BASE + '/api' + path, {
    method: options.method ?? 'GET',
    headers: {
      ...(options.body ? { 'Content-Type': 'application/json; charset=utf-8' } : {}),
      ...(token ? { Authorization: 'Bearer ' + token } : {}),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
}

export async function request<T>(path: string, options: Options = {}): Promise<T> {
  const needsAuth = options.auth !== false;
  let token = needsAuth ? (loadSession()?.accessToken ?? null) : null;

  let response = await once(path, options, token);

  // Access-токен живёт 15 минут, поэтому 401 — штатная ситуация, а не сбой
  if (response.status === 401 && needsAuth) {
    const outcome = await refreshSession();
    if (outcome.status === 'ok') {
      response = await once(path, options, outcome.token);
    } else if (outcome.status === 'network') {
      // Обновить токен помешал обрыв связи. Отдать наружу исходный 401
      // нельзя: получатель примет его за отзыв сессии и выбросит человека
      // из аккаунта. На телефоне за туннелем обрывы постоянные, и это
      // была главная причина внезапных разлогинов.
      throw new NetworkFailed();
    }
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  const payload = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const error: ApiError = payload?.code
      ? payload
      : { code: 'http_' + response.status, message: 'Ошибка запроса' };
    throw new RequestFailed(response.status, error);
  }

  return payload as T;
}

export const api = {
  checkInvite: (code: string) =>
    request<{ valid: boolean; reason: string | null }>(
      `/auth/invites/${encodeURIComponent(code)}/check`, { auth: false }),

  register: (body: unknown) =>
    request<TokenResponse>('/auth/register', { method: 'POST', body, auth: false }),

  login: (body: unknown) =>
    request<TokenResponse>('/auth/login', { method: 'POST', body, auth: false }),

  logout: () => request<void>('/auth/logout', { method: 'POST' }),

  me: () => request<import('./types').User>('/auth/me'),

  /** Только полное совпадение имени или логина: общего списка людей нет. */
  searchUsers: (query: string) =>
    request<import('./types').Person[]>(`/users/search?q=${encodeURIComponent(query)}`),

  chats: () => request<import('./types').Chat[]>('/chats'),

  createDirect: (userId: string) =>
    request<import('./types').Chat>('/chats/direct', { method: 'POST', body: { userId } }),

  createGroup: (title: string, memberIds: string[]) =>
    request<import('./types').Chat>('/chats/group', { method: 'POST', body: { title, memberIds } }),

  addMembers: (chatId: string, memberIds: string[]) =>
    request<import('./types').Chat>(`/chats/${chatId}/members`, { method: 'POST', body: { memberIds } }),

  leaveChat: (chatId: string) =>
    request<void>(`/chats/${chatId}/leave`, { method: 'POST' }),

  renameChat: (chatId: string, title: string) =>
    request<import('./types').Chat>(`/chats/${chatId}`, { method: 'PATCH', body: { title } }),

  history: (chatId: string, before?: number, limit = 50) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (before !== undefined) params.set('before', String(before));
    return request<import('./types').Message[]>(`/chats/${chatId}/messages?${params}`);
  },

  invites: () => request<import('./types').Invite[]>('/admin/invites'),

  createInvite: (body: { note?: string; role?: string; expiresInDays?: number }) =>
    request<import('./types').Invite>('/admin/invites', { method: 'POST', body }),

  /** Отзыв: срок жизни сдвигается в прошлое, строка остаётся для истории. */
  revokeInvite: (id: string) =>
    request<void>(`/admin/invites/${id}`, { method: 'DELETE' }),

  sessions: () => request<unknown[]>('/auth/sessions'),

  /** Выключить уведомления на этом устройстве, не выходя из аккаунта. */
  dropPushToken: (deviceId: string) =>
    request<void>(`/auth/devices/${deviceId}/push-token`, { method: 'DELETE' }),

  /** Проверка ссылки на смену пароля — без входа, человек как раз войти и не может. */
  checkPasswordReset: (code: string) =>
    request<{ valid: boolean; reason: string | null; displayName: string | null }>(
      `/auth/password-reset/${encodeURIComponent(code)}/check`, { auth: false }),

  resetPassword: (code: string, password: string) =>
    request<void>('/auth/password-reset', { method: 'POST', body: { code, password }, auth: false }),

  /** Администратор выдаёт участнику одноразовую ссылку на смену пароля. */
  createPasswordReset: (userId: string, expiresInHours?: number) =>
    request<{ userId: string; username: string; displayName: string; url: string; expiresAt: string }>(
      `/admin/users/${userId}/password-reset`,
      { method: 'POST', body: { expiresInHours } }),
};
